import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFViewerPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfPath, setPdfPath] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [pdfWidth, setPdfWidth] = useState(960);
  const [pdfHeight, setPdfHeight] = useState(null);
  const [pdfAspectRatio, setPdfAspectRatio] = useState(null);

  // Responsive width calculation
  useEffect(() => {
    const updateWidth = () => {
      const maxWidth = 960;
      const responsiveWidth = Math.min(window.innerWidth * 0.8, maxWidth);
      setPdfWidth(responsiveWidth);
      
      // Calculate height based on aspect ratio if available
      if (pdfAspectRatio) {
        const calculatedHeight = responsiveWidth / pdfAspectRatio;
        setPdfHeight(calculatedHeight);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [pdfAspectRatio]);

  // Project mapping
  const projectMap = {
    'customer-centric-loyalty': {
      path: '/assets/projects/Customer-Centric Loyalty & Retention.pdf',
      title: 'Customer-Centric Loyalty & Retention'
    },
    'e2e-process-assessment': {
      path: '/assets/projects/E2E Content Management Assessment.pdf',
      title: 'E2E Process Assessment'
    },
    'freight-electrification': {
      path: '/assets/projects/ElectroMarket Freight Analysis_7.19.2023.pdf',
      title: 'Freight Electrification Analysis'
    },
    'apple-carplay-cx': {
      path: '/assets/projects/Design Review - Apple CarPlay Homescreen in Sensus.pdf',
      title: 'Apple CarPlay CX Review'
    },
    'gtm-strategy-ai': {
      path: '/assets/projects/C3 Generative AI - Sales Co-Pilot.pdf',
      title: 'GTM Strategy: Generative AI Product'
    },
    'uber-global-strategy': {
      path: '/assets/projects/Uber Global Strategy Analytics Case v2.pdf',
      title: 'Uber Global Strategy Analytics Case'
    }
  };

  useEffect(() => {
    const project = projectMap[projectId];
    if (project) {
      setPdfPath(project.path);
      setProjectTitle(project.title);
    } else {
      // Redirect to projects page if project not found
      navigate('/projects');
    }
  }, [projectId, navigate]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log(`PDF loaded successfully: ${pdfPath}, pages: ${numPages}`);
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    // Calculate aspect ratio from the first page
    if (!pdfAspectRatio) {
      const viewport = page.getViewport({ scale: 1 });
      const aspectRatio = viewport.width / viewport.height;
      setPdfAspectRatio(aspectRatio);
      
      // Calculate initial height
      const calculatedHeight = pdfWidth / aspectRatio;
      setPdfHeight(calculatedHeight);
    }
  };

  const onDocumentLoadError = (error) => {
    console.error(`PDF load error for ${pdfPath}:`, error);
  };

  const nextPage = () => {
    setCurrentPage(prev => prev < numPages ? prev + 1 : 1);
  };

  const prevPage = () => {
    setCurrentPage(prev => prev > 1 ? prev - 1 : numPages);
  };

  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  // Watermark component
  const Watermark = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
      <div className="absolute inset-0">
        {Array.from({ length: 12 }, (_, i) => (
          <div
            key={i}
            className="absolute text-purple-400 font-bold text-xl transform -rotate-45"
            style={{
              left: `${(i * 20) % 100}%`,
              top: `${(i * 15) % 100}%`,
              opacity: 0.15,
              animation: `watermarkFloat ${4 + (i % 2)}s ease-in-out infinite`
            }}
          >
            HyperHumane
          </div>
        ))}
      </div>
    </div>
  );

  if (!pdfPath) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-purple-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/projects')}
                className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Projects
              </button>
            </div>
            <div className="text-center">
              <h1 className="text-xl font-semibold text-gray-900">{projectTitle}</h1>
            </div>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Page indicator */}
          {numPages && (
            <div className="bg-gray-50 px-6 py-4 border-b">
              <div className="flex justify-center">
                <div className="text-sm text-purple-800 font-medium">
                  Page {currentPage} of {numPages}
                </div>
              </div>
            </div>
          )}

          {/* PDF Display with arrows */}
          <div className="relative flex justify-center p-8">
            {/* Left arrow */}
            {numPages && numPages > 1 && (
              <button
                onClick={prevPage}
                disabled={currentPage <= 1}
                className="absolute left-8 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 p-3 rounded-full shadow-lg transition-all duration-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            
            {/* Right arrow */}
            {numPages && numPages > 1 && (
              <button
                onClick={nextPage}
                className="absolute right-8 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 p-3 rounded-full shadow-lg transition-all duration-200 z-10"
              >
                {currentPage === numPages ? <RotateCcw size={24} /> : <ChevronRight size={24} />}
              </button>
            )}

            <div className="relative">
              <Document
                file={pdfPath}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
                    <div className="text-purple-600 text-lg">Loading PDF...</div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
                    <div className="text-red-600 text-lg">Failed to load PDF</div>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  width={pdfWidth}
                  height={pdfHeight}
                  onLoadSuccess={onPageLoadSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              <Watermark />
            </div>
          </div>

          {/* Page navigation dots */}
          {numPages && numPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t">
              <div className="flex justify-center space-x-2">
                {Array.from(new Array(numPages), (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => goToPage(index + 1)}
                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                      currentPage === index + 1 ? 'bg-purple-600' : 'bg-purple-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Watermark animation CSS */}
      <style jsx>{`
        @keyframes watermarkFloat {
          0%, 100% { opacity: 0.15; transform: translateY(0px) rotate(-45deg); }
          50% { opacity: 0.25; transform: translateY(-2px) rotate(-45deg); }
        }
      `}</style>
    </div>
  );
};

export default PDFViewerPage; 